import { Alert, Form, InputGroup } from 'react-bootstrap';
import { ButtonGroup, FormGroup, PageTitleContainer, Title, BoxCard } from './styles';
import BButton from 'react-bootstrap/Button';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from '../../components/Button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUserContext } from '../../context/UserContext';
import api from '../../services/api';
import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { useAxios } from '../../services/swr';
import { Regras, FormSchemaData, changeSchemaBasedOnRules, initSchema } from './schema';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { ExtractTable } from '../../components/ExtractTable';
import { getFromSessionStorage } from '../../constants/functions';

type RegrasResponse = {
  regras: Regras;
  textos: {
    conter: Array<string>;
    naoConter: Array<string>;
  };
};

export default function ChangePassword() {
  const [errorMessage, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [schema, setSchema] = useState(initSchema);
  const [selectedQuota, setSelectedQuota] = useState('');
  const [isPasswordHiddenObject, setPasswordHidden] = useState({ oldPassword: true, newPassword: true });

  const { group, quota, loginResponseData } = useUserContext();
  const { data } = useAxios<RegrasResponse>('consorciado/regrasPoliticaAcesso', { revalidateOnFocus: false });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormSchemaData>({
    resolver: zodResolver(schema),
    defaultValues: {
      quota: '',
    },
  });

  const textTable = useMemo(() => {
    if (!data) return;

    const { conter, naoConter } = data.textos;
    const maxLength = Math.max(conter.length, naoConter.length);
    const result = [];

    for (let i = 0; i < maxLength; i++) {
      const elementA = conter[i] || '';
      const elementB = naoConter[i] || '';
      result.push([elementA, elementB]);
    }

    return result;
  }, [data]);

  useEffect(() => {
    console.log('loginResponseData:', loginResponseData);
    console.log('group:', group);
    console.log('quota:', quota);

    if (!selectedQuota && loginResponseData?.consorciados?.length) {
      const cotaAtual = loginResponseData.consorciados.find(
        (consorciado) =>
          consorciado.grupo.padStart(5, '0') === group.padStart(5, '0') &&
          consorciado.cota.padStart(4, '0') === quota.padStart(4, '0')
      );

      if (cotaAtual) {
        const grupoFormatado = cotaAtual.grupo.padStart(5, '0');
        const cotaFormatada = cotaAtual.cota.padStart(4, '0');
        const novaQuota = `${grupoFormatado}/${cotaFormatada}`;

        setSelectedQuota(novaQuota);
        setValue('quota', novaQuota);
      }
    }
  }, [loginResponseData, group, quota, selectedQuota, setValue]);

  useEffect(() => {
    console.log('selectedQuota atualizada:', selectedQuota);
  }, [selectedQuota]);

  async function handleSalvar(form: FormSchemaData) {
    setError('');
    setSuccessMessage('');

    if (!form.quota) {
      setError('Selecione uma cota antes de alterar a senha.');
      return;
    }

    const [grupo, quota] = form.quota.split('/');

    const payload = {
      grupo,
      quota,
      oldPassword: form.password,
      newPassword: form.newPassword,
      allGroups: form.alterAllQuotas ? 'S' : 'N',
    };

    try {
      const res = await api.post('/consorciado/changePassword', payload, {
        headers: { token: getFromSessionStorage('token') },
      });

      setSuccessMessage((res as any)[1]);
    } catch (e) {
      const err = e as AxiosError<any>;
      setError(err.response?.data[1] || '');
    }
  }

  async function handleChangeSchema() {
    if (!data || !loginResponseData) return;

    const { regras } = data;
    const cpfCnpj = loginResponseData.consorciadoCpfCnpj.replace(/\D/g, '');
    const idVolvo = loginResponseData.usuarioNome.trim();
    const ownCode = loginResponseData.usuarioCodigo.replace(/\D/g, '');

    setSchema(
      changeSchemaBasedOnRules(regras, {
        cpfCnpj,
        idVolvo,
        ownCode,
      })
    );
  }

  function handleQuotaChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    setSelectedQuota(value);
    setValue('quota', value);
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleSalvar)}>
        <PageTitleContainer>
          <div>
            <Title>Trocar Senha</Title>
          </div>
        </PageTitleContainer>

        {(!!errorMessage || !!successMessage) && (
          <Alert variant={errorMessage ? 'danger' : 'success'}>{errorMessage || successMessage}</Alert>
        )}

        <div className="row">
          <div className="col-md-6">
            <FormGroup controlId="formBasicTYPE">
              <Form.Label>Grupo/Cota</Form.Label>
              <Form.Select value={selectedQuota || ''} onChange={handleQuotaChange}>
                {loginResponseData?.consorciados.map((consorciado) => {
                  const grupoFormatado = consorciado.grupo.padStart(5, '0');
                  const cotaFormatada = consorciado.cota.padStart(4, '0');
                  const valorCota = `${grupoFormatado}/${cotaFormatada}`; // <--- formato alterado para /
                  const exibicaoCota = `${grupoFormatado}/${cotaFormatada}`;

                  return (
                    <option key={valorCota} value={valorCota}>
                      {exibicaoCota}.{consorciado.digito}
                    </option>
                  );
                })}
              </Form.Select>
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <FormGroup controlId="formBasicCEP">
              <Form.Label>Senha Atual</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type={isPasswordHiddenObject.oldPassword ? 'password' : 'text'}
                  placeholder="Senha Atual"
                  {...register('password')}
                  isInvalid={!!errors.password}
                />
                <BButton
                  variant="secondary"
                  onClick={() => setPasswordHidden((state) => ({ ...state, oldPassword: !state.oldPassword }))}
                >
                  {isPasswordHiddenObject.oldPassword ? <EyeSlash /> : <Eye />}
                </BButton>
                <Form.Control.Feedback type="invalid">{errors.password?.message}</Form.Control.Feedback>
              </InputGroup>
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <FormGroup controlId="streetAddress">
              <Form.Label>Nova Senha</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type={isPasswordHiddenObject.newPassword ? 'password' : 'text'}
                  placeholder="Nova Senha"
                  onFocus={handleChangeSchema}
                  {...register('newPassword')}
                  isInvalid={!!errors.newPassword}
                />
                <BButton
                  variant="secondary"
                  onClick={() => setPasswordHidden((state) => ({ ...state, newPassword: !state.newPassword }))}
                >
                  {isPasswordHiddenObject.newPassword ? <EyeSlash /> : <Eye />}
                </BButton>
                <Form.Control.Feedback type="invalid">{errors.newPassword?.message}</Form.Control.Feedback>
              </InputGroup>
            </FormGroup>
          </div>

          <div className="col-sm-6">
            <FormGroup controlId="numberAddress">
              <Form.Label>Redigite a Nova Senha</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  type={isPasswordHiddenObject.newPassword ? 'password' : 'text'}
                  placeholder="Nova Senha"
                  onFocus={handleChangeSchema}
                  {...register('newPasswordConfirmation')}
                  isInvalid={!!errors.newPasswordConfirmation}
                />
                <BButton
                  variant="secondary"
                  onClick={() => setPasswordHidden((state) => ({ ...state, newPassword: !state.newPassword }))}
                >
                  {isPasswordHiddenObject.newPassword ? <EyeSlash /> : <Eye />}
                </BButton>
                <Form.Control.Feedback type="invalid">{errors.newPasswordConfirmation?.message}</Form.Control.Feedback>
              </InputGroup>
            </FormGroup>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <Form.Check type="checkbox" label="Alterar em Todas as Cotas" {...register('alterAllQuotas')} />
          </div>
        </div>

        <ButtonGroup>
          <Button className="reset" type="button" onClick={() => reset()}>
            Restaurar
          </Button>
          <Button className="submit" type="submit">
            Salvar
          </Button>
        </ButtonGroup>
      </form>

      <BoxCard>
        <ExtractTable title="A SENHA DEVE" titleCenter rowsGroup={['CONTER', 'NÃO CONTER']} data={textTable} />
      </BoxCard>
    </>
  );
}
